/**
 * Chat Page Component
 * Following PRD Phase 3 specifications (lines 344-407)
 */

import React, { useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { useChatStore } from '@/stores/chatStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useChatSession } from '@/hooks/useChatSession';
import { DashboardLayout } from '@/components/layout';
import { checkAndClearIfNeeded } from '@/utils/clearStaleData';
import { ChatHeader } from '@/components/chat/ChatHeader';
import { MessageBubble } from '@/components/chat/MessageBubble';
import { ChatInput } from '@/components/chat/ChatInput';
import { ExampleCommands } from '@/components/chat/ExampleCommands';
import { TallyConnectionStatus } from '@/components/chat/TallyConnectionStatus';
import { PlanApproval } from '@/components/chat/PlanApproval';
import { ExecutionMonitor } from '@/components/chat/ExecutionMonitor';
import { TodoPanel } from '@/components/chat/TodoPanel';
import { StepIndicator } from '@/components/chat/StepIndicator';
import { Loader2 } from 'lucide-react';
import { CompanyFetchCoordinator } from '@/services/companyFetchCoordinator';

const Chat: React.FC = () => {
  const { toast } = useToast();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const {
    messages,
    isLoading,
    isConnected: isWebSocketConnected,
    isApiAvailable,
    isTyping,
    currentSession,
    currentPlan,
    executionUpdates,
    isExecuting,
    hasExecutionCompleted,
    sendMessage,
    clearChat,
    exportChat,
    connectWebSocket,
    disconnectWebSocket,
    approvePlan,
    clearPlan,
    // Interactive execution (like Claude Code)
    executionState,
    canInterrupt,
    interruptExecution,
    sendGuidance
  } = useChatStore();

  // Use connectionStore for Tally connection status (separate from chat connection)
  const {
    status: tallyConnectionStatus,
    lastConnected,
    deviceName,
    tallyVersion,
    companyName,
    connectionId,
    companies,
    selectedCompany,
    isLoading: isConnectionLoading,
    error: connectionError,
    checkConnection
  } = useConnectionStore();


  // Check and clear stale data on mount
  useEffect(() => {
    checkAndClearIfNeeded();
  }, []);

  // Initialize chat session (this is critical!)
  const { isSessionReady } = useChatSession();


  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      // Cleanup on unmount
      disconnectWebSocket();
    };
  }, [disconnectWebSocket]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);


  const handleSendMessage = async (text: string, fileIds?: string[]) => {
    try {
      await sendMessage(text, fileIds);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Unified refresh handler using the same mechanism as Dashboard
  const handleRefreshConnection = React.useCallback(async () => {
    if (isRefreshing) {
      return; // Prevent multiple simultaneous refreshes
    }
    
    // Clear any existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    setIsRefreshing(true);
    
    try {
      // Use coordinated refresh to prevent race conditions and duplicate calls
      await CompanyFetchCoordinator.ensureCompaniesFetched(true, 'chat-manual-refresh');
      
      // Get the updated status from the store
      const updatedState = useConnectionStore.getState();
      
      toast({
        title: "Connection refreshed",
        description: updatedState.status === 'connected' 
          ? "Connection status and companies list updated"
          : "Connection status has been updated",
      });
    } catch (error) {
      toast({
        title: "Refresh failed",
        description: "Unable to refresh connection status",
        variant: "destructive",
      });
    } finally {
      // Add debounce delay before allowing next refresh
      refreshTimeoutRef.current = setTimeout(() => {
        setIsRefreshing(false);
      }, 2000); // 2 second debounce
    }
  }, [isRefreshing, toast]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  const handleClearChat = () => {
    clearChat();
    toast({
      title: "Chat cleared",
      description: "Your chat history has been cleared.",
    });
  };

  const handleExport = (format: 'json' | 'csv' | 'txt') => {
    try {
      exportChat(format);
      toast({
        title: "Chat exported",
        description: `Your chat has been exported as ${format.toUpperCase()}.`,
      });
    } catch (error) {
      toast({
        title: "Export failed",
        description: "Failed to export chat. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleSelectCommand = (command: string) => {
    handleSendMessage(command);
  };

  const handleApprovePlan = (approved: boolean) => {
    if (currentPlan) {
      approvePlan(currentPlan.id, approved);
      if (!approved) {
        toast({
          title: "Plan Rejected",
          description: "The execution plan has been rejected.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Plan Approved",
          description: "Execution plan approved and started.",
        });
      }
    }
  };

  const handleClearPlan = () => {
    clearPlan();
  };

  return (
    <DashboardLayout>
      {/* Agent Todo Panel - slides in from right */}
      <TodoPanel />

      <div className="h-full flex flex-col">
        <Card className="flex-1 flex flex-col overflow-hidden max-h-full">
          {/* Chat Header */}
          <ChatHeader
            onClearChat={handleClearChat}
            onExport={handleExport}
            isConnected={isApiAvailable}
            messageCount={messages.length}
            tallyConnectionStatus={tallyConnectionStatus === 'not_connected' ? 'disconnected' : tallyConnectionStatus}
          />

          {/* Messages Area */}
          <ScrollArea className="flex-1 p-4" ref={scrollAreaRef}>
            {messages.length === 0 ? (
              <ExampleCommands onSelectCommand={handleSelectCommand} />
            ) : (
              <div className="space-y-4">
                {messages.map((message, index) => {
                  // Render different components based on message type
                  if (message.messageType === 'plan' && message.planData) {
                    return (
                      <div key={message.id} className="my-4 max-w-2xl mx-auto">
                        <PlanApproval
                          plan={{
                            id: message.planData.id,
                            plan_description: `Execute ${message.planData.tasks.length} tasks`,
                            tasks: message.planData.tasks.map((task: any, idx: number) => ({
                              step: idx + 1,
                              description: task.description,
                              tool: task.tool,
                              dependencies: task.dependencies.map((d: string) => parseInt(d)).filter((n: number) => !isNaN(n)),
                              risk_level: 'medium' as const,
                              estimated_time_ms: message.planData.estimated_time * 1000 / message.planData.tasks.length
                            })),
                            requires_approval: message.planData.requires_approval,
                            confidence_score: 0.85,
                            estimated_total_time_ms: message.planData.estimated_time * 1000,
                            created_at: new Date().toISOString()
                          }}
                          onApprove={() => handleApprovePlan(true)}
                          onReject={() => handleApprovePlan(false)}
                          isLoading={isExecuting}
                        />
                        {(isExecuting || hasExecutionCompleted) && currentPlan && (
                          <div className="mt-4">
                            <ExecutionMonitor
                              sessionId={currentSession?.id || ''}
                              executionId="current"
                              planId={currentPlan.id || ''}
                              tasks={currentPlan.tasks.map((task: any, index: number) => ({
                                id: task.id || `task_${index + 1}`,
                                description: task.description,
                                status: 'pending',
                                tool: task.tool,
                                progress: 0
                              }))}
                            />
                          </div>
                        )}
                      </div>
                    );
                  } else {
                    // Regular message
                    return (
                      <div key={message.id} className="max-w-2xl mx-auto">
                        <MessageBubble
                          message={message}
                          isLastMessage={index === messages.length - 1}
                        />
                      </div>
                    );
                  }
                })}
                
                {/* Typing indicator */}
                {isTyping && (
                  <div className="max-w-2xl mx-auto">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      <span>AccountsGenie is typing...</span>
                    </div>
                  </div>
                )}
                
                {/* Loading skeleton for pending response */}
                {isLoading && !isTyping && (
                  <div className="max-w-2xl mx-auto">
                    <div className="flex justify-start">
                      <div className="max-w-[70%] rounded-lg bg-muted p-4">
                        <Skeleton className="h-4 w-[250px] mb-2" />
                        <Skeleton className="h-4 w-[200px] mb-2" />
                        <Skeleton className="h-4 w-[150px]" />
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={bottomRef} />
              </div>
            )}
          </ScrollArea>



          {/* Step Progress Indicator */}
          <StepIndicator />

          {/* Input Area */}
          <div className="border-t p-3">
            <ChatInput
              onSendMessage={handleSendMessage}
              onInterrupt={interruptExecution}
              onSendGuidance={sendGuidance}
              isLoading={isLoading}
              disabled={!isApiAvailable || isExecuting}
              canInterrupt={canInterrupt}
              executionState={executionState}
              placeholder={
                !isApiAvailable ? "Connecting to AccountsGenie..." :
                isExecuting ? "Plan is executing..." :
                tallyConnectionStatus === 'connected' ? "Ask AccountsGenie about your Tally data, reports, or general questions..." :
                "Ask AccountsGenie anything... (Tally features available when TallyConnector is active)"
              }
            />
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Chat;